import { useQuery } from '@tanstack/react-query';
import { lireLesMarquesPubliques } from '@groupe-trousseau/shared/api-client';
import { CLES } from '../../shared/donnees/cles';
import { deballer } from '../../shared/api/client';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE CARROUSEL DES MARQUES — LA SEULE DONNÉE SERVEUR DE TOUT CE SITE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-70 : « **la page publique lit la base à chaque visite, et un changement paraît
 * aussitôt. Aucun cache à vider, aucun état à propager.** » Et le critère 4 en fait une
 * exigence : « QUAND une colonne déclencheuse est vidée, LE SYSTÈME DOIT faire disparaître
 * la structure **au chargement suivant**, sans aucun geste supplémentaire. »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE « AUCUN CACHE À VIDER » VEUT DIRE ICI, ET CE QU'IL NE VEUT PAS DIRE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **Ce que la règle vise est le SERVEUR**, et elle y est tenue : la route n'a ni mémoire, ni
 * durée de validité, ni invalidation à déclencher. **Elle ne vise pas le navigateur d'un
 * visiteur** — un cache de client ne peut pas rendre une marque périmée à quelqu'un
 * d'autre, et TanStack Query ne survit pas au rechargement de la page.
 *
 * **`staleTime: 0` est donc la valeur juste, et pas par prudence** : ce site est « atteint
 * une fois, par des gens qui ne connaissent pas le produit » (`DESIGN.md`), donc **la
 * seconde visite de l'accueil dans une même session de navigation est un cas rare**, et une
 * fraîcheur mise en cache n'achèterait rien de mesurable. En revanche elle coûterait
 * exactement ce que le critère 4 interdit : un chargement où la marque vidée serait encore
 * là.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **AUCUN RÉESSAI AUTOMATIQUE, ET C'EST L'INVERSE DU DÉFAUT DE TANSTACK QUERY.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Le défaut est de réessayer trois fois. **Sur une surface publique lue par n'importe qui,
 * y compris par des machines** — F-73 : « des machines qui n'appartiennent à personne
 * viennent lire l'accueil, et le carrousel lit la base » —, trois requêtes par visite en
 * font trois fois plus sur la seule route du produit qui n'est protégée par aucun compte, et
 * le produit n'a **aucun plafonnement d'appels** (D-028, D-032).
 *
 * **Le réessai est donc un geste de la personne**, porté par l'état d'erreur : « l'erreur,
 * **avec son réessai** ». Un visiteur qui réessaie décide ; une bibliothèque qui réessaie
 * multiplie une charge que rien ne borne.
 */
export function useLesMarques() {
  return useQuery({
    queryKey: CLES.marques,
    queryFn: () => deballer(lireLesMarquesPubliques()),
    // Voir ci-dessus : la fraîcheur est le critère 4 de F-70, pas un réglage de confort.
    staleTime: 0,
    retry: false,
  });
}
